import { useMemo, useState } from 'react';
import { useIeKitLabels, useIeKitIdPrefix } from '@/hooks/useIeKitLabels';
import { useRestrictedProgramLabels } from '@/hooks/useRestrictedProgramLabels';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import RemoveIcon from '@mui/icons-material/Remove';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PhonelinkRingIcon from '@mui/icons-material/PhonelinkRing';
import { WorkflowCartRow } from '@/components/shared/workflows/WorkflowCartRow';
import { IssueCartListSection } from './IssueCartListSection';
import { ScanningStatus } from '@/services/cart';
import type {
  IssueAdditionalCartItem,
  IssueBuyProgramCartItem,
  IssuePlannerCartItem,
} from './types';
import { Chip } from '@mui/material';
import {
  calculateLineTotal,
  calculateBuyProgramTotal,
  formatCurrency,
  isBuyProgramCartItemIncomplete,
} from './buyProgramPricing';
import { usePersonLabels } from '@/hooks/usePersonLabels';

export interface SizeAllocationPlaceholder {
  id: string;
  parentCatalogId: string;
  title: string;
  subtitle?: string;
  quantity: number;
}

export interface IssueCartSidebarProps {
  ieCartItems: IssuePlannerCartItem[];
  sizeAllocationPlaceholders: SizeAllocationPlaceholder[];
  additionalItems: IssueAdditionalCartItem[];
  buyProgramItems: IssueBuyProgramCartItem[];
  restrictedProgramItems: IssueBuyProgramCartItem[];
  ieAvailability: Record<string, number>;
  additionalAvailability: Record<string, number>;
  hasAddableItems: boolean;
  hasRestrictedProgramAddableItems: boolean;
  hasIncompleteRestrictedProgramItems: boolean;
  hasItems: boolean;
  plannerActionId: string | null;
  isBulkProcessing: boolean;
  isCompleting: boolean;
  /** Persistent error message from the most recent completion attempt. */
  completionError?: string | null;
  signatureAcknowledged: boolean;
  signaturePreviewSrc?: string;
  onShowCustomerSummary?: () => void;
  onAddAllAvailable: () => void;
  onAddRestrictedProgramAllAvailable: () => void;
  onUpdateRestrictedProgramPricing: (
    itemId: string,
    pricing: { actualPrice: string; tax: string; shipping: string },
  ) => void;
  onUpdateRestrictedProgramReceiptDate: (itemId: string, date: string) => void;
  onAdjustPlannerQuantity: (itemId: string, delta: number) => void;
  onRemovePlannerItem: (itemId: string) => void;
  onRemovePlaceholder: (placeholderId: string, parentCatalogId: string) => void;
  onAdjustAdditionalQuantity: (groupId: string, delta: number) => void;
  onRemoveAdditionalItem: (groupId: string) => void;
  onAdjustBuyProgramQuantity: (itemId: string, delta: number) => void;
  onRemoveBuyProgramItem: (itemId: string) => void;
  onAdjustRestrictedProgramQuantity: (itemId: string, delta: number) => void;
  onRemoveRestrictedProgramItem: (itemId: string) => void;
  onSyncToScanner: () => void;
  scanningStatus?: ScanningStatus;
  isWaitingForScan?: boolean;
  isCheckingForScanUpdates?: boolean;
  claimedByDisplayName?: string | null;
  onCaptureSignature: () => void;
  onClearSignature?: () => void;
  onSkipSignature: () => void;
  onCompleteIssue: () => void;
  onCancelSync?: () => void;
  isComptroller?: boolean;
}

export const IssueCartSidebar = ({
  ieCartItems,
  sizeAllocationPlaceholders,
  additionalItems,
  buyProgramItems,
  restrictedProgramItems,
  ieAvailability,
  additionalAvailability,
  hasAddableItems,
  hasRestrictedProgramAddableItems,
  hasIncompleteRestrictedProgramItems,
  hasItems,
  plannerActionId,
  isBulkProcessing,
  isCompleting,
  signatureAcknowledged,
  signaturePreviewSrc,
  onShowCustomerSummary,
  onAddAllAvailable,
  onAddRestrictedProgramAllAvailable,
  onUpdateRestrictedProgramPricing,
  onUpdateRestrictedProgramReceiptDate,
  onAdjustPlannerQuantity,
  onRemovePlannerItem,
  onRemovePlaceholder,
  onAdjustAdditionalQuantity,
  onRemoveAdditionalItem,
  onAdjustBuyProgramQuantity,
  onRemoveBuyProgramItem,
  onAdjustRestrictedProgramQuantity,
  onRemoveRestrictedProgramItem,
  onSyncToScanner,
  scanningStatus,
  isComptroller = false,
  completionError,
  isWaitingForScan,
  isCheckingForScanUpdates,
  claimedByDisplayName,
  onCaptureSignature,
  onClearSignature,
  onSkipSignature,
  onCompleteIssue,
  onCancelSync,
}: IssueCartSidebarProps) => {
  const person = usePersonLabels('front_window');
  const { label: ieKitLabel } = useIeKitLabels('front_window');
  const restrictedProgram = useRestrictedProgramLabels();
  const { prefix: ieKitIdPrefix } = useIeKitIdPrefix();
  const isProcessing = Boolean(plannerActionId) || isBulkProcessing;
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  // Draft actual-price input for incomplete restricted (buy) program cart rows (#1075).
  // Kept local so typing doesn't re-validate on every keystroke; committed onBlur.
  const [restrictedProgramPriceDrafts, setRestrictedProgramPriceDrafts] = useState<
    Record<string, string>
  >({});
  const isCartLocked =
    scanningStatus === ScanningStatus.ReadyToScan ||
    scanningStatus === ScanningStatus.InProgress ||
    scanningStatus === ScanningStatus.Complete;

  const linSummary = useMemo(() => {
    // Count cart items that have assets (not unique LINs)
    const ieCount = ieCartItems.filter((item) => item.assetIds.length > 0).length;
    const additionalCount = additionalItems.filter((item) => item.assetIds.length > 0).length;
    // For Buy Program, sum up the quantities
    const buyProgramCount = buyProgramItems.reduce((sum, item) => sum + item.quantity, 0);
    // For restricted (buy) program, sum up quantities separately
    const restrictedProgramCount = restrictedProgramItems.reduce(
      (sum, item) => sum + item.quantity,
      0,
    );
    // Calculate total reimbursement amount using shared utility
    const buyProgramTotal = calculateBuyProgramTotal(buyProgramItems);
    const restrictedProgramTotal = calculateBuyProgramTotal(restrictedProgramItems);

    return {
      ie: ieCount,
      additional: additionalCount,
      buyProgram: buyProgramCount,
      buyProgramTotal,
      restrictedProgramCount,
      restrictedProgramTotal,
      total: ieCount + additionalCount + buyProgramCount + restrictedProgramCount,
    };
  }, [additionalItems, ieCartItems, buyProgramItems, restrictedProgramItems]);

  return (
    <Stack spacing={3}>
      <Stack spacing={2}>
        {!isComptroller && (
          <>
            <IssueCartListSection
              title={`PHR (${ieKitIdPrefix})`}
              items={[...sizeAllocationPlaceholders, ...ieCartItems]}
              headerAction={
                hasAddableItems ? (
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={
                      isBulkProcessing ? (
                        <CircularProgress size={16} color="inherit" />
                      ) : (
                        <AddIcon />
                      )
                    }
                    onClick={() => {
                      void onAddAllAvailable();
                    }}
                    disabled={isProcessing || isCartLocked}
                    sx={{ whiteSpace: 'nowrap' }}
                  >
                    {isBulkProcessing ? 'Adding all...' : 'Add all available'}
                  </Button>
                ) : undefined
              }
              renderItem={(item) => {
                const isPlaceholder =
                  (item as SizeAllocationPlaceholder).parentCatalogId !== undefined;
                if (isPlaceholder) {
                  const placeholder = item as SizeAllocationPlaceholder;
                  const subtitleParts: string[] = [];
                  if (placeholder.subtitle) {
                    subtitleParts.push(placeholder.subtitle);
                  }
                  subtitleParts.push(`Qty ${placeholder.quantity}`);

                  return (
                    <WorkflowCartRow
                      key={placeholder.id}
                      title={placeholder.title}
                      subtitle={
                        <Stack spacing={0.5}>
                          <Chip
                            label="Awaiting size selection"
                            color="info"
                            size="small"
                            sx={{ alignSelf: 'flex-start' }}
                          />
                          <Typography variant="caption" color="text.secondary">
                            {subtitleParts.join(' • ')}
                          </Typography>
                        </Stack>
                      }
                      actions={
                        <IconButton
                          size="small"
                          color="primary"
                          aria-label="Remove item"
                          onClick={() =>
                            onRemovePlaceholder(placeholder.id, placeholder.parentCatalogId)
                          }
                          disabled={isBulkProcessing}
                        >
                          <CloseIcon fontSize="inherit" />
                        </IconButton>
                      }
                    />
                  );
                }

                const ieItem = item as IssuePlannerCartItem;
                const quantity = ieItem.assetIds.length;
                const remainingAvailability = ieAvailability[ieItem.plannerItemId] ?? 0;
                const canIncrease = remainingAvailability > 0;
                const isCartRowProcessing = plannerActionId === ieItem.id;
                const cartControlsDisabled = isCartRowProcessing || isBulkProcessing;
                const subtitleParts: string[] = [];
                const kitName = (ieItem as { kitName?: string }).kitName;
                if (kitName) {
                  subtitleParts.push(`Kit ${kitName}`);
                }
                subtitleParts.push(`Qty ${quantity}`);

                const subtitleBlock = (
                  <Stack spacing={0.25}>
                    <Typography variant="caption" color="text.secondary">
                      Location: {ieItem.sourceLocationLabel ?? 'No location'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {subtitleParts.join(' • ')}
                    </Typography>
                  </Stack>
                );

                // Compute expiration chip color/label
                const expirationDate = ieItem.expirationDate ?? null;
                let expirationChip: React.ReactNode = null;
                if (expirationDate) {
                  const today = new Date();
                  const expDate = new Date(expirationDate);
                  const daysUntil = Math.ceil(
                    (expDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24),
                  );
                  const chipColor =
                    daysUntil <= 0 ? 'error' : daysUntil <= 30 ? 'warning' : 'default';
                  const chipLabel =
                    daysUntil <= 0 ? `Expired ${expirationDate}` : `Expires ${expirationDate}`;
                  expirationChip = (
                    <Chip
                      label={chipLabel}
                      color={chipColor}
                      size="small"
                      sx={{ alignSelf: 'flex-start', mt: 0.25 }}
                    />
                  );
                }

                return (
                  <WorkflowCartRow
                    key={ieItem.id}
                    title={
                      ieItem.sizeLabel && !ieItem.name.includes(`(${ieItem.sizeLabel})`)
                        ? `${ieItem.name} (${ieItem.sizeLabel})`
                        : ieItem.name
                    }
                    subtitle={
                      expirationChip ? (
                        <Stack spacing={0.25}>
                          {subtitleBlock}
                          {expirationChip}
                        </Stack>
                      ) : (
                        subtitleBlock
                      )
                    }
                    actions={
                      <Stack direction="row" spacing={0.5} aria-label="Cart quantity controls">
                        <IconButton
                          size="small"
                          color="primary"
                          aria-label="Decrease quantity"
                          onClick={() => {
                            void onAdjustPlannerQuantity(ieItem.id, -1);
                          }}
                          disabled={quantity <= 1 || cartControlsDisabled || isCartLocked}
                        >
                          <RemoveIcon fontSize="inherit" />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="primary"
                          aria-label="Increase quantity"
                          onClick={() => {
                            void onAdjustPlannerQuantity(ieItem.id, 1);
                          }}
                          disabled={!canIncrease || cartControlsDisabled || isCartLocked}
                        >
                          <AddIcon fontSize="inherit" />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="primary"
                          aria-label="Remove from cart"
                          onClick={() => onRemovePlannerItem(ieItem.id)}
                          disabled={cartControlsDisabled || isCartLocked}
                        >
                          <CloseIcon fontSize="inherit" />
                        </IconButton>
                      </Stack>
                    }
                  />
                );
              }}
            />

            <IssueCartListSection
              title="THR (Additional Items)"
              items={additionalItems}
              renderItem={(item) => {
                const quantity = item.assetIds.length;
                const subtitleParts: string[] = [];
                subtitleParts.push(`Qty ${quantity}`);
                const subtitle = (
                  <Stack spacing={0.25}>
                    <Typography variant="caption" color="text.secondary">
                      Location: {item.sourceLocationLabel ?? 'No location'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {subtitleParts.join(' • ')}
                    </Typography>
                  </Stack>
                );
                const remainingAvailability = additionalAvailability[item.groupId] ?? 0;
                const canIncrease = remainingAvailability > 0;

                return (
                  <WorkflowCartRow
                    key={item.groupId}
                    title={
                      item.sizeLabel && !item.name.includes(`(${item.sizeLabel})`)
                        ? `${item.name} (${item.sizeLabel})`
                        : item.name
                    }
                    subtitle={subtitle}
                    actions={
                      <Stack
                        direction="row"
                        spacing={0.5}
                        aria-label="Additional quantity controls"
                      >
                        <IconButton
                          size="small"
                          color="primary"
                          aria-label="Decrease quantity"
                          onClick={() => onAdjustAdditionalQuantity(item.groupId, -1)}
                          disabled={quantity <= 1 || isBulkProcessing || isCartLocked}
                        >
                          <RemoveIcon fontSize="inherit" />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="primary"
                          aria-label="Increase quantity"
                          onClick={() => onAdjustAdditionalQuantity(item.groupId, 1)}
                          disabled={!canIncrease || isBulkProcessing || isCartLocked}
                        >
                          <AddIcon fontSize="inherit" />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="primary"
                          aria-label="Remove from cart"
                          onClick={() => onRemoveAdditionalItem(item.groupId)}
                          disabled={isBulkProcessing || isCartLocked}
                        >
                          <CloseIcon fontSize="inherit" />
                        </IconButton>
                      </Stack>
                    }
                  />
                );
              }}
            />
          </>
        )}

        <IssueCartListSection
          title="Buy Program"
          items={buyProgramItems}
          renderItem={(item) => {
            const { catalog, quantity, actualPrice, tax, shipping } = item;
            const canIncrease =
              typeof item.maxQuantity !== 'number' || quantity < Math.max(item.maxQuantity, 1);
            // Calculate line total using shared utility
            const lineTotal = calculateLineTotal(item);

            return (
              <WorkflowCartRow
                key={item.id}
                title={catalog.name}
                subtitle={
                  <Stack spacing={0.25}>
                    <Typography variant="caption" color="text.secondary">
                      Price: {formatCurrency(actualPrice)}/ea
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Qty: {quantity}
                    </Typography>
                    {tax > 0 && (
                      <Typography variant="caption" color="text.secondary">
                        Tax: {formatCurrency(tax)}/ea
                      </Typography>
                    )}
                    {shipping > 0 && (
                      <Typography variant="caption" color="text.secondary">
                        Shipping: {formatCurrency(shipping)}/ea
                      </Typography>
                    )}
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      Line Total: {formatCurrency(lineTotal)}
                    </Typography>
                  </Stack>
                }
                actions={
                  <Stack direction="row" spacing={0.5} aria-label="Buy Program quantity controls">
                    <IconButton
                      size="small"
                      color="primary"
                      aria-label="Decrease quantity"
                      onClick={() => onAdjustBuyProgramQuantity(item.id, -1)}
                      disabled={quantity <= 1 || isCartLocked}
                    >
                      <RemoveIcon fontSize="inherit" />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="primary"
                      aria-label="Increase quantity"
                      onClick={() => onAdjustBuyProgramQuantity(item.id, 1)}
                      disabled={!canIncrease || isCartLocked}
                    >
                      <AddIcon fontSize="inherit" />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="primary"
                      aria-label="Remove buy program item"
                      onClick={() => onRemoveBuyProgramItem(item.id)}
                      disabled={isCartLocked}
                    >
                      <CloseIcon fontSize="inherit" />
                    </IconButton>
                  </Stack>
                }
              />
            );
          }}
        />

        <IssueCartListSection
          title={restrictedProgram.programLabel}
          items={restrictedProgramItems}
          headerAction={
            hasRestrictedProgramAddableItems ? (
              <Button
                variant="outlined"
                size="small"
                startIcon={
                  isBulkProcessing ? <CircularProgress size={16} color="inherit" /> : <AddIcon />
                }
                onClick={() => {
                  void onAddRestrictedProgramAllAvailable();
                }}
                disabled={isProcessing || isCartLocked}
                sx={{ whiteSpace: 'nowrap' }}
              >
                {isBulkProcessing ? 'Adding all...' : 'Add all available'}
              </Button>
            ) : undefined
          }
          renderItem={(item) => {
            const { catalog, quantity, actualPrice, tax, shipping } = item;
            const canIncrease =
              typeof item.maxQuantity !== 'number' || quantity < Math.max(item.maxQuantity, 1);
            const lineTotal = calculateLineTotal(item);
            const isIncomplete = isBuyProgramCartItemIncomplete(item);

            return (
              <WorkflowCartRow
                key={item.id}
                title={catalog.name}
                subtitle={
                  <Stack spacing={0.5}>
                    {isIncomplete ? (
                      <>
                        <Chip
                          label="Needs price & receipt date"
                          color="warning"
                          size="small"
                          sx={{ alignSelf: 'flex-start' }}
                        />
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                          <TextField
                            label="Actual price"
                            size="small"
                            value={
                              restrictedProgramPriceDrafts[item.id] ??
                              (actualPrice > 0 ? String(actualPrice) : '')
                            }
                            onChange={(e) => {
                              const value = e.target.value;
                              setRestrictedProgramPriceDrafts((prev) => ({
                                ...prev,
                                [item.id]: value,
                              }));
                            }}
                            onBlur={() => {
                              onUpdateRestrictedProgramPricing(item.id, {
                                actualPrice: restrictedProgramPriceDrafts[item.id] ?? '',
                                tax: String(tax),
                                shipping: String(shipping),
                              });
                            }}
                            disabled={isCartLocked}
                            InputProps={{
                              startAdornment: <InputAdornment position="start">$</InputAdornment>,
                            }}
                            sx={{ width: 110 }}
                          />
                          <TextField
                            label="Receipt date"
                            type="date"
                            size="small"
                            value={item.receiptDate ?? ''}
                            onChange={(e) => {
                              onUpdateRestrictedProgramReceiptDate(item.id, e.target.value);
                            }}
                            disabled={isCartLocked}
                            slotProps={{ inputLabel: { shrink: true } }}
                            sx={{ width: 150 }}
                          />
                        </Stack>
                      </>
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        Price: {formatCurrency(actualPrice)}/ea
                      </Typography>
                    )}
                    <Typography variant="caption" color="text.secondary">
                      Qty: {quantity}
                    </Typography>
                    {tax > 0 && (
                      <Typography variant="caption" color="text.secondary">
                        Tax: {formatCurrency(tax)}/ea
                      </Typography>
                    )}
                    {shipping > 0 && (
                      <Typography variant="caption" color="text.secondary">
                        Shipping: {formatCurrency(shipping)}/ea
                      </Typography>
                    )}
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      Line Total: {formatCurrency(lineTotal)}
                    </Typography>
                  </Stack>
                }
                actions={
                  <Stack
                    direction="row"
                    spacing={0.5}
                    aria-label={restrictedProgram.quantityControlsAriaLabel}
                  >
                    <IconButton
                      size="small"
                      color="primary"
                      aria-label="Decrease quantity"
                      onClick={() => onAdjustRestrictedProgramQuantity(item.id, -1)}
                      disabled={quantity <= 1 || isCartLocked}
                    >
                      <RemoveIcon fontSize="inherit" />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="primary"
                      aria-label="Increase quantity"
                      onClick={() => onAdjustRestrictedProgramQuantity(item.id, 1)}
                      disabled={!canIncrease || isCartLocked}
                    >
                      <AddIcon fontSize="inherit" />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="primary"
                      aria-label={restrictedProgram.removeItemAriaLabel}
                      onClick={() => onRemoveRestrictedProgramItem(item.id)}
                      disabled={isCartLocked}
                    >
                      <CloseIcon fontSize="inherit" />
                    </IconButton>
                  </Stack>
                }
              />
            );
          }}
        />
      </Stack>

      <Stack spacing={1.5}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          Summary
        </Typography>
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack spacing={0.5}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Line Items Total
            </Typography>
            {!isComptroller && (
              <>
                <Typography variant="body2">
                  {ieKitLabel} items issued: {linSummary.ie}
                </Typography>
                <Typography variant="body2">
                  Additional items issued: {linSummary.additional}
                </Typography>
              </>
            )}
            <Typography variant="body2">
              Buy Program items issued: {linSummary.buyProgram}
            </Typography>
            {linSummary.buyProgramTotal > 0 && (
              <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main' }}>
                Reimbursement Total: {formatCurrency(linSummary.buyProgramTotal)}
              </Typography>
            )}
            {linSummary.restrictedProgramCount > 0 && (
              <Typography variant="body2">
                {restrictedProgram.programLabel} items issued: {linSummary.restrictedProgramCount}
              </Typography>
            )}
            {linSummary.restrictedProgramTotal > 0 && (
              <Typography variant="body2" sx={{ fontWeight: 600, color: 'secondary.main' }}>
                {restrictedProgram.reimbursementTotalLabel}:{' '}
                {formatCurrency(linSummary.restrictedProgramTotal)}
              </Typography>
            )}
            <Divider sx={{ my: 0.5 }} />
            <Typography variant="body2" color="text.secondary">
              Total issued:{' '}
              {isComptroller
                ? linSummary.buyProgram + linSummary.restrictedProgramCount
                : linSummary.total}
            </Typography>
          </Stack>
        </Paper>
      </Stack>

      <Stack spacing={2}>
        {!isComptroller && (
          <>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Step 1 • Sync to Scanner
            </Typography>
            <Button
              variant="contained"
              color="primary"
              fullWidth
              onClick={onSyncToScanner}
              disabled={!hasItems || isWaitingForScan || scanningStatus === ScanningStatus.Complete}
            >
              {scanningStatus === ScanningStatus.Complete ? 'Synced' : 'Sync Picklist'}
            </Button>

            {/* Scanning Status Indicator */}
            {scanningStatus && scanningStatus !== ScanningStatus.NotStarted && (
              <Paper
                variant="outlined"
                sx={{
                  p: 1.5,
                  bgcolor: (t) =>
                    scanningStatus === ScanningStatus.Complete
                      ? t.palette.mode === 'dark'
                        ? 'rgba(63, 127, 85, 0.18)'
                        : 'success.50'
                      : t.palette.mode === 'dark'
                        ? 'rgba(32, 28, 81, 0.25)'
                        : 'primary.50',
                  borderColor:
                    scanningStatus === ScanningStatus.Complete ? 'success.200' : 'primary.200',
                }}
              >
                <Stack direction="row" spacing={1.5} alignItems="center">
                  {scanningStatus === ScanningStatus.Complete ? (
                    <CheckCircleIcon sx={{ color: 'success.main', fontSize: 20 }} />
                  ) : (
                    <PhonelinkRingIcon
                      sx={{
                        color: 'primary.main',
                        fontSize: 20,
                        animation: 'pulse 2s ease-in-out infinite',
                        '@keyframes pulse': {
                          '0%, 100%': { opacity: 1 },
                          '50%': { opacity: 0.4 },
                        },
                      }}
                    />
                  )}
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
                      {scanningStatus === ScanningStatus.ReadyToScan &&
                        'Waiting for mobile scanner'}
                      {scanningStatus === ScanningStatus.InProgress &&
                        (claimedByDisplayName
                          ? `Scanning in progress by ${claimedByDisplayName}`
                          : 'Scanning in progress')}
                      {scanningStatus === ScanningStatus.Complete && 'Scanning complete'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {scanningStatus === ScanningStatus.ReadyToScan &&
                        'Open the picklist on mobile to begin scanning'}
                      {scanningStatus === ScanningStatus.InProgress &&
                        (claimedByDisplayName
                          ? `${claimedByDisplayName} is scanning items on mobile device`
                          : 'Items are being scanned on mobile device')}
                      {scanningStatus === ScanningStatus.Complete &&
                        'All items have been verified. Ready to complete.'}
                    </Typography>
                  </Box>
                  {isWaitingForScan && (
                    <CircularProgress
                      size={16}
                      thickness={5}
                      sx={{
                        color: 'primary.main',
                        opacity: isCheckingForScanUpdates ? 1 : 0.3,
                      }}
                    />
                  )}
                </Stack>
              </Paper>
            )}
            {isWaitingForScan && onCancelSync && (
              <Button
                variant="outlined"
                color="error"
                fullWidth
                size="small"
                onClick={() => setShowCancelConfirm(true)}
              >
                Cancel Sync
              </Button>
            )}
          </>
        )}
        {isComptroller && (
          <>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Step 1 • View Summary
            </Typography>
            {onShowCustomerSummary ? (
              <Button
                variant="outlined"
                fullWidth
                onClick={onShowCustomerSummary}
                disabled={!hasItems || isCompleting}
              >
                View Issue Summary
              </Button>
            ) : null}
          </>
        )}
        {!isComptroller && (
          <>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Step 2 • {person.label} Signature
            </Typography>
            {onShowCustomerSummary ? (
              <Button
                variant="outlined"
                fullWidth
                onClick={onShowCustomerSummary}
                disabled={!hasItems || isCompleting}
              >
                View Issue Summary
              </Button>
            ) : null}
            <Button
              variant="outlined"
              fullWidth
              onClick={onCaptureSignature}
              disabled={!hasItems || isCompleting}
            >
              {signatureAcknowledged && signaturePreviewSrc
                ? 'Signature Captured'
                : signatureAcknowledged
                  ? 'Signature Skipped'
                  : `Capture ${person.label} Signature`}
            </Button>
            {!signatureAcknowledged && (
              <Button
                variant="text"
                size="small"
                fullWidth
                onClick={onSkipSignature}
                disabled={!hasItems || isCompleting}
                sx={{ mt: -0.5 }}
              >
                Skip Signature
              </Button>
            )}
            {signaturePreviewSrc ? (
              <Stack spacing={0.5}>
                <Paper variant="outlined" sx={{ p: 1, bgcolor: '#ffffff' }}>
                  <Box
                    component="img"
                    src={signaturePreviewSrc}
                    alt="Captured signature preview"
                    sx={{
                      display: 'block',
                      width: '100%',
                      maxHeight: 120,
                      objectFit: 'contain',
                    }}
                  />
                </Paper>
                {onClearSignature ? (
                  <Button
                    size="small"
                    variant="text"
                    color="inherit"
                    onClick={onClearSignature}
                    sx={{ alignSelf: 'flex-start' }}
                  >
                    Clear signature
                  </Button>
                ) : null}
              </Stack>
            ) : null}
          </>
        )}
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          {isComptroller ? 'Step 2 • Complete' : 'Step 3 • Complete'}
        </Typography>
        {completionError && (
          <Alert severity="error" variant="outlined" sx={{ fontSize: '0.813rem' }}>
            {completionError}
          </Alert>
        )}
        {hasIncompleteRestrictedProgramItems && (
          <Alert severity="warning" variant="outlined" sx={{ fontSize: '0.813rem' }}>
            Enter an actual price and receipt date for every {restrictedProgram.programLabel} item
            before completing the issue.
          </Alert>
        )}
        <Tooltip
          title={
            hasIncompleteRestrictedProgramItems
              ? `Enter price & receipt date for all ${restrictedProgram.programLabel} items first`
              : !hasItems
                ? 'Add items to the cart first'
                : !signatureAcknowledged && !isComptroller
                  ? 'Complete Step 2 (Signature) first'
                  : isWaitingForScan
                    ? 'Waiting for scanner to complete'
                    : ''
          }
          arrow
        >
          <span>
            <Button
              variant="contained"
              color="secondary"
              fullWidth
              onClick={onCompleteIssue}
              disabled={
                !hasItems ||
                (!signatureAcknowledged && !isComptroller) ||
                isCompleting ||
                isWaitingForScan ||
                hasIncompleteRestrictedProgramItems
              }
            >
              {isCompleting
                ? 'Completing...'
                : isComptroller
                  ? 'Email 1034 to SM'
                  : 'Complete Issue'}
            </Button>
          </span>
        </Tooltip>
      </Stack>
      <Dialog open={showCancelConfirm} onClose={() => setShowCancelConfirm(false)}>
        <DialogTitle>Cancel Sync?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Cancelling will stop the active mobile scan session. Any scanning progress will be lost,
            but cart contents will be preserved.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCancelConfirm(false)}>Keep Scanning</Button>
          <Button
            color="error"
            onClick={() => {
              setShowCancelConfirm(false);
              onCancelSync?.();
            }}
          >
            Cancel Sync
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};
